'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'system',
  setTheme: () => {},
  resolvedTheme: 'light',
});

export const useTheme = () => useContext(ThemeContext);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>('system');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
  const [mounted, setMounted] = useState(false);
  const { user } = useAuth();

  // Apply theme to document
  function applyTheme(mode: ThemeMode) {
    const resolved = mode === 'system' ? getSystemTheme() : mode;
    setResolvedTheme(resolved);
    if (resolved === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  // On mount: load from localStorage first
  useEffect(() => {
    setMounted(true);
    const stored = (localStorage.getItem('studymate-theme') as ThemeMode) || 'system';
    setThemeState(stored);
    applyTheme(stored);
  }, []);

  // When user logs in, sync from Supabase profile
  useEffect(() => {
    if (!user || !mounted) return;
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('theme_preference')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.theme_preference) {
          const dbTheme = data.theme_preference as ThemeMode;
          setThemeState(dbTheme);
          localStorage.setItem('studymate-theme', dbTheme);
          applyTheme(dbTheme);
        }
      })
      .catch(() => {});
  }, [user, mounted]);

  // Listen for system theme changes
  useEffect(() => {
    if (!mounted) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (theme === 'system') applyTheme('system');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, mounted]);

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    localStorage.setItem('studymate-theme', newTheme);
    applyTheme(newTheme);
    // Persist to Supabase if logged in
    if (user) {
      const supabase = createClient();
      supabase
        .from('user_profiles')
        .update({ theme_preference: newTheme })
        .eq('id', user.id)
        .then(() => {})
        .catch(() => {});
    }
  };

  if (!mounted) return <>{children}</>;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
